/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 21-08-2026
 *
 * De vaste indeling van het docentendashboard (golf 3f): welke actielog-acties en elementtypen
 * op welke serie tellen, en hoe elke serie eruitziet. Overgenomen uit `_actietypes`,
 * `_elementtypes` en `balken` in de oude `dashboard.directive.js`.
 *
 * De teksten staan hier bewust letterlijk en niet als vertaalsleutel: het paneel kende er in ng1
 * ook geen, en vertalen is een functionele uitbreiding (los eindje in `migratieplan.md`).
 * Wél gelijkgetrokken (testronde 21-08-2026): ng1 mengde Engels en Nederlands door elkaar
 * (`Action: create` naast `Acties: Entiteit`). Elk serielabel begint nu met de naam van zijn
 * eigen balk — `Actie:`, `Type:`, `Norm:` — dezelfde woorden als op de knop en de y-as.
 */

/**
 * Een balk in de grafiek: een eigen y-as, een eigen stapel en een eigen aan/uit-knop.
 */
export type BalkKey = 'acties' | 'elementen' | 'norm';

/**
 * De balken in weergavevolgorde, met hun knoplabel.
 */
export const BALKEN: ReadonlyArray<{key: BalkKey; knop: string}> = [
    {key: 'acties', knop: 'Actie'},
    {key: 'elementen', knop: 'Type'},
    {key: 'norm', knop: 'Norm'}
];

/**
 * De randkleur van de balkjes per balk; laat in de grafiek zien bij welke as een stapel hoort.
 */
export const RANDKLEUR: Readonly<Record<BalkKey, string>> = {
    acties: 'rgba(100,100,100,0.6)',
    elementen: 'rgba(52,133,52,0.6)',
    norm: 'rgba(200,0,0,0.6)'
};

/**
 * Eén serie: een gekleurd segment in de gestapelde balk plus (meestal) een knopje om hem aan en
 * uit te zetten.
 */
export interface Serie {
    /** Sleutel van de serie, uniek over alle balken heen. */
    seriekey: string;
    /** De balk waar de serie in valt: bepaalt y-as, stapel en randkleur. */
    balk: BalkKey;
    /** De vulkleur van het segment en van het knopje. */
    kleur: string;
    /** Het label in de tooltip van de grafiek. */
    label: string;
    /** De tooltip op het knopje. */
    titel?: string;
    /** Font-awesome-klasse als het knopje een icoon toont. */
    icoon?: string;
    /** Naam van de svg-afbeelding als het knopje een plaatje toont. */
    svg?: string;
    /** Het knopje krijgt witte inhoud in plaats van zwarte (donkere achtergrond). */
    tekstwit?: boolean;
    /** De serie staat bij het openen uit. */
    standaardVerborgen?: boolean;
    /** Volgt de zichtbaarheid van die serie en krijgt zelf geen knopje. */
    volgt?: string;
    /** Telt niet mee in de donut. */
    nietInDonut?: boolean;
}

/**
 * Welke actielog-acties op welke actieserie tellen. Een actie die nergens in staat telt als
 * `rest`; de acties onder `_skip` tellen nergens mee.
 */
export const ACTIETYPES: Readonly<Record<string, readonly string[]>> = {
    create: ['create'],
    modify: ['modify'],
    'delete': ['delete'],
    model: ['copymodel', 'newmodel', 'modifymodel'],
    sim: ['start_sim', 'stop_sim', 'sim_nextstep', 'sim_select', 'sim_multiselect', 'sim_setselected', 'sim_toggle_ih', 'sim_toggle_vh'],
    undoredo: ['undo', 'redo'],
    condities: ['cond_new', 'cond_add', 'cond_reset', 'cond_set', 'cond_delete', 'cond_rename', 'cond_view'],
    fouten: ['help', 'foutselect', 'simfb', 'simfb_select', 'simfb_show'],
    _skip: ['init_app', 'errormodel', 'play_video', 'cancel_video']
};

/**
 * Welke elementtypen op welke elementserie tellen. Een gelogd targettype telt voor een serie als
 * het een subtype is van een van de genoemde typen; wat nergens onder valt telt niet mee, net
 * als de typen onder `_skip`. In ng1 stond hier `null` waar de serienaam zelf het type is —
 * hier staat dat type gewoon uitgeschreven.
 */
export const ELEMENTTYPES: Readonly<Record<string, readonly string[]>> = {
    entity: ['entity'],
    agent: ['agent'],
    assumption: ['assumption'],
    quantity: ['quantity'],
    quantity_space: ['quantity_space', 'quantity_space_element'],
    attribute: ['attribute'],
    configuration: ['configuration'],
    value: ['value'],
    correspondence: ['correspondence'],
    q_exo: ['q_exo', 'quantity_allvalues'],
    ineq: ['ineq'],
    calc: ['calc'],
    proportionality: ['proportionality'],
    influence: ['influence'],
    _skip: ['model', 'sim', 'app', 'transitie', 'state']
};

/**
 * De normcategorieën waarvoor een goed/fout/missend-drieluik bestaat, in weergavevolgorde.
 * De sleutels komen overeen met die in de opgeslagen normtelling.
 */
const NORMCATEGORIEEN: ReadonlyArray<{key: string; kleur: string; naam: string; svg: string; tekstwit?: boolean}> = [
    {key: 'entity', kleur: '85,5,39', naam: 'Entiteit', svg: 'entity'},
    {key: 'configuration', kleur: '244,71,8', naam: 'Configuratie', svg: 'configuration', tekstwit: true},
    {key: 'quantity', kleur: '104,142,38', naam: 'Grootheid', svg: 'quantity'},
    {key: 'ineq', kleur: '245,26,164', naam: 'Ongelijkheid', svg: 'ineq'},
    {key: 'correspondence', kleur: '161,7,2', naam: 'Correspondentie', svg: 'correspondence'},
    {key: 'proportionality', kleur: '60,219,211', naam: 'Proportionaliteit', svg: 'proportionality'},
    {key: 'influence', kleur: '60,86,219', naam: 'Invloed', svg: 'influence'}
];

/**
 * Bouwt het goed/fout/missend-drieluik voor één normcategorie. De drie delen delen dezelfde
 * kleur in drie sterktes en één knopje: fout en missend volgen de zichtbaarheid van goed.
 */
function normSeries(categorie: typeof NORMCATEGORIEEN[number]): Serie[] {
    const goedkey = `${categorie.key}_goed`;
    const basis = {balk: 'norm' as const, nietInDonut: true};
    return [
        {
            ...basis,
            seriekey: goedkey,
            kleur: `rgba(${categorie.kleur},0.75)`,
            label: `Norm: ${categorie.naam} goed`,
            titel: `Norm: ${categorie.naam}`,
            svg: categorie.svg,
            tekstwit: categorie.tekstwit
        },
        {
            ...basis,
            seriekey: `${categorie.key}_fout`,
            kleur: `rgba(${categorie.kleur},0.5)`,
            label: `Norm: ${categorie.naam} fout`,
            volgt: goedkey
        },
        {
            ...basis,
            seriekey: `${categorie.key}_missend`,
            kleur: `rgba(${categorie.kleur},0.2)`,
            label: `Norm: ${categorie.naam} missend`,
            volgt: goedkey
        }
    ];
}

/**
 * Alle series in weergavevolgorde: eerst de acties, dan de elementtypen, dan de norm. Deze
 * volgorde bepaalt de stapelvolgorde in de grafiek en de volgorde van de knopjes.
 */
export const SERIES: ReadonlyArray<Serie> = [
    {seriekey: 'create', balk: 'acties', kleur: 'rgba(126,189,194,0.75)', label: 'Actie: aanmaken', titel: 'Actie: aanmaken', icoon: 'fa-solid fa-plus'},
    {seriekey: 'modify', balk: 'acties', kleur: 'rgba(226,115,150,0.75)', label: 'Actie: wijzigen', titel: 'Actie: wijzigen', icoon: 'fa-solid fa-pencil'},
    {seriekey: 'delete', balk: 'acties', kleur: 'rgba(255,229,217,0.75)', label: 'Actie: verwijderen', titel: 'Actie: verwijderen', icoon: 'fa-solid fa-trash-can'},
    {seriekey: 'undoredo', balk: 'acties', kleur: 'rgba(251,186,114,0.75)', label: 'Actie: ongedaan maken', titel: 'Actie: ongedaan maken', icoon: 'fa-solid fa-arrow-rotate-left', standaardVerborgen: true},
    {seriekey: 'condities', balk: 'acties', kleur: 'rgba(255,190,11,0.75)', label: 'Actie: condities', titel: 'Actie: condities', icoon: 'fa-solid fa-code-branch', standaardVerborgen: true},
    {seriekey: 'model', balk: 'acties', kleur: 'rgba(219,213,110,0.75)', label: 'Actie: model', titel: 'Actie: model', icoon: 'fa-solid fa-file', standaardVerborgen: true},
    {seriekey: 'sim', balk: 'acties', kleur: 'rgba(240,207,101,0.75)', label: 'Actie: simuleren', titel: 'Actie: simuleren', icoon: 'fa-solid fa-chevron-right', standaardVerborgen: true},
    {seriekey: 'fouten', balk: 'acties', kleur: 'rgba(255,125,0,0.84)', label: 'Actie: fouten', titel: 'Actie: fouten', icoon: 'fa-regular fa-circle-question', standaardVerborgen: true},
    {seriekey: 'rest', balk: 'acties', kleur: 'rgba(84,84,84,0.75)', label: 'Actie: overig', titel: 'Actie: overig', icoon: 'fa-solid fa-ellipsis', tekstwit: true, standaardVerborgen: true},

    {seriekey: 'entity', balk: 'elementen', kleur: 'rgba(85,5,39,0.75)', label: 'Type: Entiteit', titel: 'Type: Entiteit', svg: 'entity'},
    {seriekey: 'agent', balk: 'elementen', kleur: 'rgba(68,5,85,0.75)', label: 'Type: Agent', titel: 'Type: Agent', svg: 'agent'},
    {seriekey: 'assumption', balk: 'elementen', kleur: 'rgba(44,5,85,0.75)', label: 'Type: Assumptie', titel: 'Type: Assumptie', svg: 'assumption'},
    {seriekey: 'quantity', balk: 'elementen', kleur: 'rgba(104,142,38,0.75)', label: 'Type: Grootheid', titel: 'Type: Grootheid', svg: 'quantity'},
    {seriekey: 'quantity_space', balk: 'elementen', kleur: 'rgba(188,189,192,0.75)', label: 'Type: Waardenbereik', titel: 'Type: Waardenbereik', svg: 'quantity_space'},
    {seriekey: 'attribute', balk: 'elementen', kleur: 'rgba(250,166,19,0.75)', label: 'Type: Attribuut', titel: 'Type: Attribuut', svg: 'attribute'},
    {seriekey: 'configuration', balk: 'elementen', kleur: 'rgba(244,71,8,0.75)', label: 'Type: Configuratie', titel: 'Type: Configuratie', svg: 'configuration'},
    {seriekey: 'value', balk: 'elementen', kleur: 'rgba(161,7,2,0.75)', label: 'Type: Waarde', titel: 'Type: Waarde', svg: 'value'},
    {seriekey: 'correspondence', balk: 'elementen', kleur: 'rgba(161,74,2,0.75)', label: 'Type: Correspondentie', titel: 'Type: Correspondentie', svg: 'correspondence'},
    {seriekey: 'q_exo', balk: 'elementen', kleur: 'rgba(0,148,198,0.75)', label: 'Type: Exogeen', titel: 'Type: Exogeen', svg: 'q_exo'},
    {seriekey: 'ineq', balk: 'elementen', kleur: 'rgba(245,26,164,0.75)', label: 'Type: Ongelijkheid', titel: 'Type: Ongelijkheid', svg: 'ineq'},
    {seriekey: 'calc', balk: 'elementen', kleur: 'rgba(188,189,192,0.75)', label: 'Type: Calculus', titel: 'Type: Calculus', svg: 'calc'},
    {seriekey: 'proportionality', balk: 'elementen', kleur: 'rgba(60,219,211,0.75)', label: 'Type: Proportionaliteit', titel: 'Type: Proportionaliteit', svg: 'proportionality'},
    {seriekey: 'influence', balk: 'elementen', kleur: 'rgba(60,86,219,0.75)', label: 'Type: Invloed', titel: 'Type: Invloed', svg: 'influence'},

    ...NORMCATEGORIEEN.flatMap(normSeries)
];

/**
 * Serielookup op sleutel, voor de tellingen en de normcategorieën.
 */
export const SERIE_PER_KEY: ReadonlyMap<string, Serie> = new Map(SERIES.map(serie => [serie.seriekey, serie]));

/**
 * De series die bij het openen uitstaan: de standaard verborgen series plus de series die er
 * een volgen.
 */
export function standaardVerborgen(): Set<string> {
    const verborgen = new Set(SERIES.filter(serie => serie.standaardVerborgen).map(serie => serie.seriekey));
    for (const serie of SERIES) {
        if (serie.volgt && verborgen.has(serie.volgt)) {
            verborgen.add(serie.seriekey);
        }
    }
    return verborgen;
}
